import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { Button, Modal } from '../..';
import { UberHealthTag } from '../../common/UberHealthTag/UberHealthTag';
import {
  DUPLICATE_RIDE_MODAL_SUBTITLE,
  DUPLICATE_RIDE_MODAL_TITLE,
} from '../../../constants/constants';
import { DateHelper } from '../../../utils';
import styles from './index.module.css';

const formatRideDate = (datetime, timezone) => {
  if (!datetime) return '—';
  const zoned = timezone
    ? DateHelper.utcToZonedTime(datetime, timezone)
    : new Date(datetime);
  return DateHelper.format(zoned, 'MMM d, yyyy');
};

const formatRideTime = (datetime, timezone) => {
  if (!datetime) return '—';
  const zoned = timezone
    ? DateHelper.utcToZonedTime(datetime, timezone)
    : new Date(datetime);
  return DateHelper.format(zoned, 'h:mm a');
};

const getOverlapBadgeLabel = (overlappingLegs) => {
  if (overlappingLegs === 'A') return 'Leg A Ride Overlaps';
  if (overlappingLegs === 'B') return 'Leg B Ride Overlaps';
  if (overlappingLegs === 'BOTH') return 'Leg A & B Ride Overlaps';
  return null;
};

export const DuplicateRideModal = ({
  visible,
  onCancel,
  onContinue,
  riderName = '',
  duplicates = [],
  isRecurring = false,
  isRoundtrip = false,
  loading = false,
}) => {
  const showLegBColumn = isRoundtrip;

  const showCount = isRecurring && !isRoundtrip;
  const existingLabelText =
    isRecurring && isRoundtrip
      ? 'Existing ride(s) series for this rider'
      : 'Existing ride(s) for this rider';

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      closable
      width='clamp(480px, calc(100vw * 480 / 1258), 560px)'
      title={null}
      zIndex={2000}>
      <div className={styles.modalContent}>
        <h2 className={styles.title}>{DUPLICATE_RIDE_MODAL_TITLE}</h2>
        <p className={styles.subtitle}>{DUPLICATE_RIDE_MODAL_SUBTITLE}</p>

        <div className={styles.metaRow}>
          <div className={styles.rider}>
            Rider : <span className={styles.riderName}>{riderName || '—'}</span>
          </div>
          <div className={styles.existingLabel}>
            {existingLabelText}
            {showCount && (
              <>
                : <span className={styles.existingCount}>{duplicates.length}</span>
              </>
            )}
          </div>
        </div>

        <div className={styles.table}>
          <div
            className={classNames(styles.tableHeader, {
              [styles.withLegB]: showLegBColumn,
            })}>
            <span>Ride date</span>
            <span className={classNames(styles.legCell, styles.legACell)}>Leg A</span>
            {showLegBColumn && (
              <span className={classNames(styles.legCell, styles.legBCell)}>Leg B</span>
            )}
          </div>

          {duplicates.map((ride) => {
            const badge = showLegBColumn ? getOverlapBadgeLabel(ride.overlappingLegs) : null;
            return (
              <div key={ride.rideId} className={styles.tableRowBlock}>
                <div
                  className={classNames(styles.tableRow, {
                    [styles.withLegB]: showLegBColumn,
                  })}>
                  <span className={styles.rideDateCell}>
                    {formatRideDate(ride.rideDate || ride.legAPickupTime, ride.timezone)}
                    {ride.isUberRide && <UberHealthTag className={styles.uberTag} />}
                  </span>
                  <span className={classNames(styles.legCell, styles.legACell)}>
                    {formatRideTime(ride.legAPickupTime, ride.timezone)}
                  </span>
                  {showLegBColumn && (
                    <span className={classNames(styles.legCell, styles.legBCell)}>
                      <span className={styles.legBTime}>
                        {formatRideTime(ride.legBReturnTime, ride.timezone)}
                      </span>
                      {badge && (
                        <span className={styles.overlapBadge}>{badge}</span>
                      )}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className={styles.actions}>
          <Button
            type='button'
            buttonType='default'
            myClassName={styles.cancelBtn}
            onClick={onCancel}
            disabled={loading}>
            Cancel
          </Button>
          <Button
            type='button'
            myClassName={styles.continueBtn}
            onClick={onContinue}
            loading={loading}>
            Continue Anyway
          </Button>
        </div>
      </div>
    </Modal>
  );
};

DuplicateRideModal.propTypes = {
  visible: PropTypes.bool,
  onCancel: PropTypes.func,
  onContinue: PropTypes.func,
  riderName: PropTypes.string,
  duplicates: PropTypes.array,
  isRecurring: PropTypes.bool,
  isRoundtrip: PropTypes.bool,
  loading: PropTypes.bool,
};
